import { Key } from './primitives.js';

export enum ExpressionType {
  Answer = 'answer',
  Boolean = 'boolean',
  Compare = 'compare',
  Condition = 'condition',
  Format = 'format',
  List = 'list',
  Literal = 'literal',
  Math = 'math',
  Date = 'date',
}

export interface AnswerExpression {
  type: ExpressionType.Answer;
  key: Key;
}

export enum BoolOperator {
  And = 'and',
  Or = 'or',
  Not = 'not',
}

export type BooleanExpression =
  | {
      type: ExpressionType.Boolean;
      op: BoolOperator.And | BoolOperator.Or;
      expressions: Expression[];
    }
  | { type: ExpressionType.Boolean; op: BoolOperator.Not; expression: Expression };

export enum CompareOperator {
  Eq = 'eq',
  Gt = 'gt',
  Gte = 'gte',
  In = 'in',
  Lt = 'lt',
  Lte = 'lte',
}

export interface CompareExpression {
  type: ExpressionType.Compare;
  op: CompareOperator;
  comparators: [Expression, Expression];
}

export interface ConditionExpression {
  type: ExpressionType.Condition;
  conditions: {
    if: Expression;
    then: Expression;
  }[];
  else?: Expression;
}

export enum FormatType {
  Pattern = 'pattern',
  Phone = 'phone',
}

export type FormatExpression =
  | {
      type: ExpressionType.Format;
      format: Exclude<FormatType, 'pattern'>;
      value: Expression;
    }
  | {
      type: ExpressionType.Format;
      format: FormatType.Pattern;
      pattern: string;
      value: Expression;
    };

export enum ListOperator {
  Length = 'length',
  Map = 'map',
  Sum = 'sum',
}

export type ListExpression =
  | {
      type: ExpressionType.List;
      op: ListOperator.Length | ListOperator.Sum;
      list: Expression;
    }
  | {
      type: ExpressionType.List;
      op: ListOperator.Map;
      list: Expression;
      mapper: Expression;
    };

export type AstValue = string | number | boolean;

export interface LiteralExpression {
  type: ExpressionType.Literal;
  value: AstValue | AstValue[];
}

export enum MathOperator {
  Add = 'add',
  Divide = 'divide',
  Multiply = 'multiply',
  Subtract = 'subtract',
}

export interface MathExpression {
  type: ExpressionType.Math;
  op: MathOperator;
  expressions: [Expression, Expression];
}

export enum DateOperator {
  DaysInMonth = 'daysInMonth',
  DaysRemainingInMonth = 'daysRemainingInMonth',
  DiffInDays = 'diffInDays',
}

export type DateExpression =
  | {
      type: ExpressionType.Date;
      op: DateOperator.DaysInMonth | DateOperator.DaysRemainingInMonth;
      date: Expression;
    }
  | {
      type: ExpressionType.Date;
      op: DateOperator.DiffInDays;
      dates: [Expression, Expression];
    };

export type Expression =
  | AnswerExpression
  | BooleanExpression
  | CompareExpression
  | ConditionExpression
  | FormatExpression
  | ListExpression
  | LiteralExpression
  | MathExpression
  | DateExpression;
